import { Box, type BoxProps, Container, Stack, Typography } from '@mui/material';
import type { LinkProps } from '@tanstack/react-router';
import { useGetDefaultReservedHeaderSpace } from 'hooks/useResponsive';
import { type ReactNode, Suspense } from 'react';
import { CenteredSpinner } from './CenteredSpinner';
import { FillingBox } from './filling/FillingBox';
import { GoBackLink } from './link/GoBackLink';
import { MoreActionsMenu } from './MoreActionsMenu';
import Page from './Page';

export type PageContainerProps = BoxProps & {
  pageTitle: string;
  pageTitleBadge?: ReactNode;
  pageSubtitle?: string | ReactNode;
  showGoBackLink?: boolean;
  goBackUrl?: LinkProps['to'];
  topRightButtons?: ReactNode;
  moreMenuActions?: ReactNode;
  isLoading?: boolean;
  pageInfoSection?: ReactNode;
  onPageTitleClick?: VoidFunction;
};

export default function PageContainer({
  pageTitle,
  pageTitleBadge,
  pageSubtitle,
  topRightButtons,
  moreMenuActions,
  isLoading,
  pageInfoSection,
  onPageTitleClick,
  children,
  showGoBackLink = true,
  goBackUrl,
}: PageContainerProps) {
  // margins handled by the upper level layout
  const reservedSpace = useGetDefaultReservedHeaderSpace();

  return (
    <Page title={pageTitle} style={{ height: `calc(100vh - ${reservedSpace}px)`, display: 'flex' }}>
      <Container maxWidth="xl" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          gap={8}
          sx={{ mb: 2 }}
        >
          <Box>
            {showGoBackLink && <GoBackLink goBackUrl={goBackUrl} />}
            <Box>
              <Typography
                variant="h3"
                component="span"
                onClick={onPageTitleClick}
                sx={{ '&:hover': { cursor: onPageTitleClick ? 'pointer' : 'default' } }}
                data-testid="page-title"
              >
                {pageTitle}
              </Typography>
              {pageTitleBadge && (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', verticalAlign: 'middle', ml: 1, mb: 1.5 }}
                  gap={1}
                >
                  {pageTitleBadge}
                </Box>
              )}
            </Box>
            {pageSubtitle && (
              <Typography variant="h5" color="GrayText">
                {pageSubtitle}
              </Typography>
            )}
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2}>
            {topRightButtons}
            {moreMenuActions && <MoreActionsMenu>{moreMenuActions}</MoreActionsMenu>}
          </Stack>
        </Stack>
        {pageInfoSection}
        <Suspense fallback={<CenteredSpinner />}>
          {isLoading ? <CenteredSpinner /> : <FillingBox>{children}</FillingBox>}
        </Suspense>
      </Container>
    </Page>
  );
}
